/**
 * Shared chrome for WP-D's twenty frames (flows D, E, F and H1–H3).
 *
 * Why it lives under `routes/offering/`: WP-D owns exactly seven route directories
 * (`offering`, `cart`, `checkout`, `activity`, `account`, `provider`, `policy`) and
 * nothing else, so a sibling `routes/shared/` would be outside the package. `offering`
 * is flow D's first directory, so the shared pieces sit there and the other six import
 * from it rather than each keeping its own copy of the same transcription.
 *
 * Everything here is read off `design/Oprappo P0 Screens.dc.html`. Nothing is designed.
 * Where a value repeats across frames it is written once here; where a frame differs,
 * the frame writes its own markup and this file is not used.
 */
import type { ReactNode } from 'react';
import { FRAME_BOX } from '../../responsive/frame.js';
import { IconButton } from '@oprappo/ui';

/**
 * Every phone frame in the P0 file is the same box:
 *
 *     <div style="background:#F4F0E7;border:1px solid #E2DED5;height:680px;
 *                 display:flex;flex-direction:column;box-sizing:border-box">
 *
 * with `width:360px` on the wrapper. Background, border and padding differ per frame
 * (D4 is `1.5px solid #0E3A45`; D6 carries its own padding), so they are props.
 */
export function Frame({
  children,
  className,
}: {
  children: ReactNode;
  className?: string | undefined;
}): JSX.Element {
  return (
    <div
      className={
        FRAME_BOX +
        (className === undefined ? '' : ' ' + className)
      }
    >
      {children}
    </div>
  );
}

/** `background:#F4F0E7;border:1px solid #E2DED5` — every WP-D frame but D4. */
export const SAND_FRAME = 'bg-sand border border-border';

/**
 * The white screen header: D3, D4, D7, E4, F2, F3, F4, F5, F6 all draw
 * `background:#fff;border-bottom:1px solid #E2DED5;padding:14px 16px;
 *  display:flex;gap:12px;align-items:center` with a 20px back chevron and a
 * `600 18px/1.75` ink title.
 *
 * The back control is `IconButton control="back"`, so the § 01 accessible name
 * `পিছনে যান` is used rather than invented. Its glyph is the design system's
 * `chevronLeft` (`M14 6l-6 6 6 6`); the P0 frames draw `M15 5l-7 7 7 7`. The two paths
 * differ by a pixel at the same 24-grid and 2px weight — reported, not reconciled here.
 */
export function ScreenHeader({
  titleBn,
  onBack,
  trailing,
}: {
  titleBn: string;
  onBack?: (() => void) | undefined;
  trailing?: ReactNode | undefined;
}): JSX.Element {
  return (
    <div className="bg-white border-b border-border px-[16px] py-[14px] flex gap-[12px] items-center">
      <IconButton control="back" size={20} onClick={onBack} />
      <div className="font-anek font-semibold text-[18px] leading-[1.75] text-ink flex-1 min-w-0">
        {titleBn}
      </div>
      {trailing}
    </div>
  );
}

/**
 * `M12 5v14M5 12h14`, 2px, round caps, in teal — D5's "নতুন কার্ড যোগ করুন" (18px),
 * F3's "নতুন ঠিকানা যোগ করুন" and F4's card row (both 20px).
 *
 * There is no `plus` in `@oprappo/ui`'s `IconName`. Transcribed from the frames and
 * reported as a gap rather than added to the package, which WP-D does not own.
 */
export function PlusIcon({ size }: { size: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
      <path d="M12 5v14M5 12h14" stroke="#0E3A45" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

/**
 * `width:20px;height:20px;border:2px solid …;border-radius:50%` with a 10px teal dot —
 * D5's payment options and F5's language and mode rows.
 *
 * `@oprappo/ui`'s `Sheet` draws an 18px radio with a 9px dot and does not export it, so
 * the 20px form the P0 frames use is transcribed here. Reported.
 *
 * `box-content` is not decoration. The design canvas sets `box-sizing:border-box` on the
 * frame boxes and NOWHERE else (42 occurrences in the P0 file, every one a frame), so
 * `width:20px;border:2px` there is a 24px ring. This app's reset makes everything
 * `border-box`, which would draw it 20px — 2px narrower on every side. Same rule
 * wherever a P0 element carries an explicit size AND a border.
 */
export function Radio({ selected }: { selected: boolean }): JSX.Element {
  return (
    <div
      className={
        'w-[20px] h-[20px] box-content border-2 rounded-full flex items-center justify-center flex-none ' +
        (selected ? 'border-teal' : 'border-border')
      }
    >
      {selected ? <div className="w-[10px] h-[10px] bg-teal rounded-full" /> : null}
    </div>
  );
}

/**
 * The row chevron the F1 list and the E4 problem cards draw: `M9 6l6 6-6 6` in
 * `#5B6B69`. `@oprappo/ui`'s `chevronRight` is `M10 6l6 6-6 6` — one pixel apart at the
 * same weight. Transcribed from the frame; reported alongside the back-arrow difference.
 */
export function RowChevron({ size }: { size: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
      <path
        d="M9 6l6 6-6 6"
        stroke="#5B6B69"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** `M6 9l6 6 6-6` in `#5B6B69` — the select chevron on F2's area field and F5's country. */
export function SelectChevron({ size }: { size: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
      <path
        d="M6 9l6 6 6-6"
        stroke="#5B6B69"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The Oprappo mark — two strokes, teal then amber, `stroke-width:12`, round caps and
 * joins. Drawn at 30px on D7's receipt.
 *
 * WP-C transcribed the same eight lines into `routes/onboarding/frame.tsx`; neither
 * package can import the other's, and `@oprappo/ui` does not export the mark. That the
 * brand's own logotype is duplicated per work package is reported as a packages/ui gap.
 */
export function Mark({
  size,
  className,
}: {
  size: number;
  className?: string | undefined;
}): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-label="Oprappo mark"
      className={className}
    >
      <path
        d="M24 76 L24 40 Q24 24 40 24 L50 24"
        stroke="#0E3A45"
        strokeWidth="12"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M76 24 L76 60 Q76 76 60 76 L50 76"
        stroke="#E8A33D"
        strokeWidth="12"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The partner role sentence, drawn on D5 and F4:
 * "অপ্রাপ্য ক্যাশ পরিচালনা করে অংশীদার প্রতিষ্ঠান। অপ্রাপ্য নিজে ব্যাংক নয়।"
 *
 * Oprappo holds no bank, MFS or wallet licence, so no money screen may be silent about
 * who does. D4, D6 and D7 are money screens that do not draw this line; HANDOFF § 0.5
 * and docs/05 § fulfilment admit no exception, so it is rendered there too — using this
 * exact drawn string. No new Bangla is written anywhere. Every added instance is listed
 * in the WP-D report as a deviation from the frame.
 */
export const PARTNER_ROLE_BN =
  'অপ্রাপ্য ক্যাশ পরিচালনা করে অংশীদার প্রতিষ্ঠান। অপ্রাপ্য নিজে ব্যাংক নয়।';

/**
 * The disclosure as D5 draws it: `background:#F4F0E7;border:1px solid #E2DED5;
 * padding:11px 13px`, text `400 13px/1.75` in body.
 *
 * `partnerName` is the payload's `partner_name` (docs/05 § fulfilment: "the client must
 * display it on every money screen"). No frame draws a slot or a label for it, so it is
 * rendered as a bare second line rather than wrapped in copy that does not exist.
 */
export function PartnerDisclosure({
  partnerName,
  className,
}: {
  partnerName: string | null;
  className?: string | undefined;
}): JSX.Element {
  return (
    <div
      className={
        'bg-sand border border-border px-[13px] py-[11px]' +
        (className === undefined ? '' : ' ' + className)
      }
    >
      <div className="font-anek font-normal text-[13px] leading-[1.75] text-body">
        {PARTNER_ROLE_BN}
      </div>
      {partnerName === null ? null : (
        <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted">
          {partnerName}
        </div>
      )}
    </div>
  );
}

/**
 * The two-family stack the P0 file uses for strings that mix Latin figures with Bangla
 * words: `Manrope,'Anek Bangla',sans-serif`. Manrope sets the figures, Anek Bangla the
 * Bangla — the fallback is deliberate and is transcribed rather than collapsed to one
 * family. The tokens preset names each family separately and has no class for the pair.
 *
 * This is NOT the fix for a Bangla-numeral string: those must be Anek Bangla outright
 * (docs/02 § numerals), which is `font-anek`.
 */
export const MANROPE_THEN_ANEK = "Manrope,'Anek Bangla',sans-serif";

/**
 * The same stack as a class rather than a `style` attribute.
 *
 * `apps/web/public/_headers` carries the CSP, and every style ATTRIBUTE React renders is
 * governed by `style-src`. Tailwind's arbitrary-value syntax compiles this pair into the
 * built stylesheet instead, so the policy does not have to allow inline styles for it.
 * The `family-name:` hint is what tells Tailwind this is a family and not a weight; the
 * underscore is Tailwind's escape for the space in `Anek Bangla`. The emitted rule is
 * `font-family:Manrope,'Anek Bangla',sans-serif` — the value above, unchanged.
 */
export const MANROPE_THEN_ANEK_CLASS = "font-[family-name:Manrope,'Anek_Bangla',sans-serif]";
